/* ==========================================================================
   ASPOOM · intro cinematográfica
   --------------------------------------------------------------------------
   Portada a pantalla completa -> vídeo con sonido -> la tienda sube.

   Reglas de diseño de este módulo:
   · Un solo disparo. El estado es una máquina explícita, así que ningún clic
     repetido, tecla o evento duplicado puede lanzar la secuencia dos veces.
   · Nunca deja al usuario encerrado. Todo camino de error (vídeo que no
     carga, autoplay bloqueado, buffer que se atasca, `ended` que no llega)
     termina en el revelado de la tienda.
   · El sonido sólo suena después de un clic real del usuario, y siempre
     con un control para silenciarlo.
   · Sólo se animan `transform` y `opacity`.

   El estado inicial (mostrar o no la intro) lo decide un script en línea
   dentro de la sección, antes del primer pintado. Aquí sólo se ejecuta la
   secuencia.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT = document.documentElement;
  var STORAGE_KEY = 'aspoom_intro_seen';
  var MUTE_KEY = 'aspoom_intro_muted';

  function store(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* modo privado o cookies bloqueadas: la intro simplemente se repetirá */
    }
  }

  function read(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function AspoomIntro(el) {
    this.el = el;
    this.video = el.querySelector('[data-aspoom-video]');
    this.cover = el.querySelector('[data-aspoom-cover]');
    this.startButton = el.querySelector('[data-aspoom-start]');
    this.skipButton = el.querySelector('[data-aspoom-skip]');
    this.soundButton = el.querySelector('[data-aspoom-sound]');
    this.site = document.getElementById('site-content');

    this.revealDuration = parseInt(el.dataset.revealDuration, 10) || 1350;
    this.bufferTimeout = parseInt(el.dataset.bufferTimeout, 10) || 7000;
    this.stallTimeout = parseInt(el.dataset.stallTimeout, 10) || 6000;

    // Cuánto antes del final arranca el revelado. El vídeo termina con
    // varias décimas de negro puro; esperar a `ended` deja la pantalla
    // apagada más tiempo del necesario.
    this.revealLead = parseInt(el.dataset.revealLead, 10) || 0;

    this.soundEnabled = el.dataset.sound === 'true';
    this.userMuted = read(MUTE_KEY) === '1';

    this.state = 'cover';
    this.videoFailed = false;
    this.timers = [];
    this.bound = [];

    this.init();
  }

  AspoomIntro.prototype = {
    /* --------------------------------------------------------------- */

    init: function () {
      var self = this;

      this.lockSite();
      this.syncSoundButton();

      if (this.video) {
        // El vídeo no se descarga hasta que sabemos que la intro corre.
        this.video.muted = true;
        this.video.defaultMuted = true;
        this.video.playsInline = true;
        this.video.preload = 'auto';
        try {
          this.video.load();
        } catch (e) {
          /* algunos navegadores lanzan si no hay fuentes; se gestiona en `error` */
        }

        // HAVE_CURRENT_DATA: ya hay un fotograma que pintar. A partir de
        // aquí el vídeo en pausa ES la portada y el marcador desenfocado
        // de debajo queda tapado. El usuario percibe un enfoque, no un
        // cambio de imagen, porque es el mismo encuadre.
        var markReady = function () {
          self.el.classList.add('is-ready');
        };

        if (this.video.readyState >= 2) {
          markReady();
        } else {
          this.once(this.video, 'loadeddata', markReady);
          this.once(this.video, 'canplay', markReady);
        }

        this.on(this.video, 'ended', function () {
          self.reveal({ blackout: false, reason: 'video-ended' });
        });

        this.on(this.video, 'timeupdate', function () {
          self.armStallWatchdog();
          // Se recalcula en cada tic: absorbe atascos, saltos y cambios de
          // velocidad sin que el corte se adelante nunca.
          self.scheduleLeadReveal();
        });

        // Un fallo del vídeo NO debe hacer desaparecer la portada por su
        // cuenta: sólo se anota. Si el usuario ya había pulsado, se pasa al
        // revelado; si aún no, la portada se queda y el clic la resuelve.
        var sources = this.video.querySelectorAll('source');
        var failures = 0;

        var onFail = function () {
          self.videoFailed = true;
          if (self.state === 'loading') {
            self.reveal({ blackout: true, reason: 'video-error' });
          }
        };

        this.on(this.video, 'error', onFail);

        Array.prototype.forEach.call(sources, function (source) {
          self.on(source, 'error', function () {
            failures += 1;
            // Sólo cuenta como fallo real cuando ninguna fuente sirve.
            if (failures >= sources.length) onFail();
          });
        });
      }

      // Un único punto de escucha para todo el contenedor de la portada:
      // el botón vive dentro, así que su clic (también el sintetizado por
      // Enter/Espacio) burbujea hasta aquí sin duplicar el disparo.
      this.on(this.cover, 'click', function () {
        self.start();
      });

      if (this.skipButton) {
        this.on(this.skipButton, 'click', function (event) {
          event.stopPropagation();
          self.reveal({ blackout: true, reason: 'skip' });
        });
      }

      if (this.soundButton) {
        this.on(this.soundButton, 'click', function (event) {
          event.stopPropagation();
          self.toggleSound();
        });
      }

      this.on(document, 'keydown', function (event) {
        if (self.state === 'done') return;

        if (event.key === 'Escape') {
          event.preventDefault();
          self.state === 'cover' ? self.start() : self.reveal({ blackout: true, reason: 'escape' });
          return;
        }

        // «M» para silenciar, como en cualquier reproductor.
        if ((event.key === 'm' || event.key === 'M') && self.state === 'playing') {
          event.preventDefault();
          self.toggleSound();
          return;
        }

        // Enter / Espacio con el foco en el lienzo (no en un botón real).
        if (
          self.state === 'cover' &&
          (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') &&
          (document.activeElement === self.el || document.activeElement === self.cover)
        ) {
          event.preventDefault();
          self.start();
        }
      });

      // Si el usuario cambia de pestaña, el navegador puede pausar el vídeo.
      this.on(document, 'visibilitychange', function () {
        if (self.state === 'playing' && document.visibilityState === 'visible' && self.video) {
          var resume = self.video.play();
          if (resume && resume.catch) resume.catch(function () {});
        }
      });

      if (this.el.hasAttribute('tabindex')) {
        this.el.focus({ preventScroll: true });
      }

      this.el.dispatchEvent(new CustomEvent('aspoom:intro:ready', { bubbles: true }));
    },

    /* --- sonido ------------------------------------------------------ */

    wantsSound: function () {
      return this.soundEnabled && !this.userMuted;
    },

    syncSoundButton: function () {
      if (!this.soundButton) return;
      var on = this.wantsSound();
      this.soundButton.setAttribute('aria-pressed', on ? 'true' : 'false');
      this.soundButton.classList.toggle('is-muted', !on);
      var label = this.soundButton.dataset[on ? 'labelOn' : 'labelOff'];
      if (label) this.soundButton.setAttribute('aria-label', label);
    },

    toggleSound: function () {
      if (!this.soundEnabled || !this.video) return;
      this.userMuted = !this.userMuted;
      store(MUTE_KEY, this.userMuted ? '1' : '0');
      this.syncSoundButton();

      if (this.userMuted) {
        this.fadeVolume(0, 180, function (video) {
          video.muted = true;
        });
      } else {
        this.video.muted = false;
        this.fadeVolume(1, 260);
      }
    },

    /* Rampa de volumen: evita el chasquido de pasar de 0 a 1 de golpe. */
    fadeVolume: function (target, duration, done) {
      var video = this.video;
      if (!video) return;

      clearInterval(this.volumeTimer);

      if (video.muted && target > 0) video.muted = false;

      var from = video.volume;
      var start = null;
      var self = this;

      if (duration <= 0 || from === target) {
        video.volume = target;
        if (done) done(video);
        return;
      }

      this.volumeTimer = setInterval(function () {
        if (start === null) start = Date.now();
        var progress = Math.min(1, (Date.now() - start) / duration);
        try {
          video.volume = from + (target - from) * progress;
        } catch (e) {
          progress = 1;
        }
        if (progress >= 1) {
          clearInterval(self.volumeTimer);
          if (done) done(video);
        }
      }, 25);
    },

    /* --- paso 2 y 3: vídeo ------------------------------------------- */

    start: function () {
      if (this.state !== 'cover') return;
      this.state = 'loading';

      if (!this.video || this.videoFailed) {
        // Sin vídeo utilizable, el clic lleva directo a la tienda.
        this.reveal({ blackout: true, reason: this.video ? 'video-error' : 'no-video' });
        return;
      }

      var self = this;
      store(STORAGE_KEY, '1');

      var begin = function () {
        if (self.state !== 'loading') return;
        self.playNow();
      };

      // HAVE_FUTURE_DATA: hay al menos el fotograma actual y el siguiente.
      if (this.video.readyState >= 3) {
        begin();
      } else {
        this.el.classList.add('is-loading');
        this.once(this.video, 'canplay', begin);
        this.once(this.video, 'canplaythrough', begin);
        this.setTimer(function () {
          if (self.state === 'loading') {
            self.reveal({ blackout: true, reason: 'buffer-timeout' });
          }
        }, this.bufferTimeout);
      }
    },

    playNow: function () {
      var self = this;
      var withSound = this.wantsSound();

      // Se arranca a volumen 0 y se sube: el sonido entra a la vez que la
      // portada se funde, sin golpe seco.
      if (withSound) {
        this.video.muted = false;
        try {
          this.video.volume = 0;
        } catch (e) {
          /* algunos navegadores móviles no dejan tocar el volumen */
        }
      }

      var onStarted = function () {
        if (self.state !== 'loading') return;
        self.state = 'playing';
        self.el.classList.remove('is-loading');
        self.el.classList.add('is-playing');

        if (self.wantsSound() && !self.video.muted) self.fadeVolume(1, 320);

        self.armStallWatchdog();
        self.scheduleLeadReveal();
        self.setTimer(function () {
          if (self.state === 'playing') {
            self.reveal({ blackout: true, reason: 'hard-cap' });
          }
        }, (self.video.duration ? self.video.duration * 1000 : 10000) + 5000);
      };

      var playMuted = function () {
        // El navegador ha bloqueado el sonido: se reintenta en silencio
        // antes de dar la reproducción por perdida.
        self.video.muted = true;
        var retry = self.video.play();
        if (retry && retry.then) {
          retry.then(onStarted).catch(function () {
            self.reveal({ blackout: true, reason: 'autoplay-blocked' });
          });
        } else {
          onStarted();
        }
      };

      var promise = this.video.play();

      if (promise && promise.then) {
        promise.then(onStarted).catch(withSound ? playMuted : function () {
          self.reveal({ blackout: true, reason: 'autoplay-blocked' });
        });
      } else {
        // Navegadores antiguos: `play()` no devuelve promesa.
        this.once(this.video, 'playing', onStarted);
        this.setTimer(onStarted, 400);
      }
    },

    /* El vídeo acaba con varias décimas de negro puro. Revelar en cuanto
       la imagen ya es negra evita una pantalla apagada de más.

       Se usa un temporizador, no el propio `timeupdate`: ese evento sólo
       llega unas cuatro veces por segundo, así que por sí solo recortaría
       bastante menos de lo pedido. El temporizador se rearma en cada tic y
       comprueba el tiempo real antes de disparar, de modo que un atasco
       nunca corta imagen todavía visible. */
    scheduleLeadReveal: function () {
      var self = this;
      clearTimeout(this.leadTimer);

      if (this.state !== 'playing' || !this.revealLead || !this.video) return;

      var remaining = (this.video.duration - this.video.currentTime) * 1000;
      if (!isFinite(remaining)) return;

      var wait = remaining - this.revealLead;
      if (wait <= 0) {
        this.reveal({ blackout: false, reason: 'lead' });
        return;
      }

      this.leadTimer = setTimeout(function () {
        var left = (self.video.duration - self.video.currentTime) * 1000;
        if (isFinite(left) && left > self.revealLead + 120) {
          self.scheduleLeadReveal();
          return;
        }
        self.reveal({ blackout: false, reason: 'lead' });
      }, wait);

      this.timers.push(this.leadTimer);
    },

    armStallWatchdog: function () {
      var self = this;
      clearTimeout(this.stallTimer);
      if (this.state !== 'playing') return;
      this.stallTimer = setTimeout(function () {
        if (self.state === 'playing') {
          self.reveal({ blackout: true, reason: 'stalled' });
        }
      }, this.stallTimeout);
      this.timers.push(this.stallTimer);
    },

    /* --- paso 4: la tienda sube -------------------------------------- */

    reveal: function (options) {
      if (this.state === 'revealing' || this.state === 'done') return;
      options = options || {};

      this.state = 'revealing';
      this.clearTimers();

      var self = this;
      var run = function () {
        ROOT.classList.add('aspoom-revealing');

        if (!self.site) {
          self.finish();
          return;
        }

        var settle = function (event) {
          if (event && event.propertyName !== 'transform') return;
          self.site.removeEventListener('transitionend', settle);
          self.finish();
        };

        self.site.addEventListener('transitionend', settle);
        // Red de seguridad por si el navegador se come el transitionend.
        self.setTimer(function () {
          self.site.removeEventListener('transitionend', settle);
          self.finish();
        }, self.revealDuration + 500);
      };

      if (options.blackout) {
        // Al saltar, el vídeo puede estar en cualquier fotograma: se pasa
        // por negro para que el revelado arranque siempre desde negro,
        // igual que en el final natural del vídeo.
        this.el.classList.add('is-blackout');
        this.fadeVolume(0, 200);
        if (this.video && !this.video.paused) {
          this.setTimer(function () {
            self.video.pause();
          }, 220);
        }
        this.setTimer(run, 230);
      } else {
        // Final natural: el vídeo sigue corriendo bajo el revelado (ya es
        // negro) y su cola de audio se apaga en rampa. Así no se corta
        // en seco ni la imagen ni el sonido.
        this.fadeVolume(0, Math.min(600, this.revealDuration));
        run();
      }

      this.el.dispatchEvent(
        new CustomEvent('aspoom:intro:reveal', {
          bubbles: true,
          detail: { reason: options.reason || 'video-ended' },
        })
      );
    },

    finish: function () {
      if (this.state === 'done') return;
      this.state = 'done';
      this.clearTimers();
      clearInterval(this.volumeTimer);

      ROOT.classList.remove('aspoom-intro-on', 'aspoom-revealing');
      ROOT.classList.add('aspoom-revealed');

      this.unlockSite();

      this.el.classList.add('is-done');
      this.el.setAttribute('aria-hidden', 'true');
      this.el.setAttribute('hidden', '');

      if (this.video) {
        this.video.pause();
        this.video.muted = true;
        // Libera el buffer: son varios MB que ya no hacen falta.
        this.video.removeAttribute('src');
        while (this.video.firstChild) this.video.removeChild(this.video.firstChild);
        try {
          this.video.load();
        } catch (e) {
          /* nada que cargar */
        }
      }

      store(STORAGE_KEY, '1');
      window.scrollTo(0, 0);

      var main = document.getElementById('MainContent');
      if (main && typeof main.focus === 'function') main.focus({ preventScroll: true });

      document.dispatchEvent(new CustomEvent('aspoom:intro:done'));
    },

    /* --- bloqueo del sitio ------------------------------------------- */

    lockSite: function () {
      if (!this.site) return;
      if ('inert' in HTMLElement.prototype) {
        this.site.inert = true;
      } else {
        this.site.setAttribute('aria-hidden', 'true');
      }
    },

    unlockSite: function () {
      if (!this.site) return;
      if ('inert' in HTMLElement.prototype) {
        this.site.inert = false;
      }
      this.site.removeAttribute('aria-hidden');
    },

    /* --- utilidades --------------------------------------------------- */

    on: function (target, type, handler) {
      if (!target) return;
      target.addEventListener(type, handler);
      this.bound.push([target, type, handler]);
    },

    once: function (target, type, handler) {
      if (!target) return;
      var wrapped = function (event) {
        target.removeEventListener(type, wrapped);
        handler(event);
      };
      target.addEventListener(type, wrapped);
      this.bound.push([target, type, wrapped]);
    },

    setTimer: function (fn, delay) {
      var id = setTimeout(fn, delay);
      this.timers.push(id);
      return id;
    },

    clearTimers: function () {
      this.timers.forEach(clearTimeout);
      this.timers = [];
      clearTimeout(this.stallTimer);
      clearTimeout(this.leadTimer);
    },

    teardown: function () {
      this.clearTimers();
      clearInterval(this.volumeTimer);
      this.bound.forEach(function (entry) {
        entry[0].removeEventListener(entry[1], entry[2]);
      });
      this.bound = [];
    },
  };

  /* ------------------------------------------------------------------ */
  /* Arranque                                                            */
  /* ------------------------------------------------------------------ */

  var instance = null;

  function boot() {
    var el = document.querySelector('[data-aspoom-intro]');
    if (!el) return;

    if (!ROOT.classList.contains('aspoom-intro-on') || prefersReducedMotion()) {
      // La intro no corre: se retira la capa y la tienda queda tal cual.
      el.classList.add('is-done');
      el.setAttribute('hidden', '');
      el.setAttribute('aria-hidden', 'true');
      ROOT.classList.remove('aspoom-intro-on');
      ROOT.classList.add('aspoom-revealed');
      return;
    }

    instance = new AspoomIntro(el);
  }

  function replay() {
    var el = document.querySelector('[data-aspoom-intro]');
    if (!el || prefersReducedMotion()) return;

    if (instance) instance.teardown();

    el.removeAttribute('hidden');
    el.removeAttribute('aria-hidden');
    // `is-ready` la vuelve a poner init() en la misma tarea si el vídeo ya
    // tiene datos, así que no llega a repintarse sin ella.
    el.classList.remove('is-done', 'is-playing', 'is-loading', 'is-blackout', 'is-ready');

    // `aspoom-intro-off` tiene que irse: en el editor de temas hay una
    // regla que oculta la intro mientras esa clase esté puesta, y la
    // repetición es justo el camino que usa el editor.
    ROOT.classList.remove('aspoom-revealed', 'aspoom-revealing', 'aspoom-intro-off');
    ROOT.classList.add('aspoom-intro-on');

    var video = el.querySelector('[data-aspoom-video]');
    if (video) {
      var template = el.querySelector('[data-aspoom-sources]');
      if (template && !video.querySelector('source')) {
        video.appendChild(template.content.cloneNode(true));
      }

      try {
        video.load();
      } catch (e) {
        /* sin fuentes todavía */
      }

      // Rebobinar sólo funciona con los metadatos ya leídos; si se hace
      // antes, la repetición arranca por donde se quedó la anterior.
      var rewind = function () {
        try {
          video.currentTime = 0;
        } catch (e) {
          /* el navegador aún no permite buscar */
        }
      };

      if (video.readyState >= 1) {
        rewind();
      } else {
        video.addEventListener('loadedmetadata', rewind, { once: true });
      }
    }

    instance = new AspoomIntro(el);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // API mínima para QA y para el editor de temas.
  window.AspoomIntro = {
    replay: replay,
    reveal: function () {
      if (instance) instance.reveal({ blackout: true, reason: 'api' });
    },
    get state() {
      return instance ? instance.state : 'idle';
    },
  };

  // Editor de temas: la intro se reproduce al seleccionar su sección y se
  // retira al salir, para no bloquear la edición del resto de la portada.
  document.addEventListener('shopify:section:select', function (event) {
    if (event.target.querySelector('[data-aspoom-intro]')) replay();
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    if (event.target.querySelector('[data-aspoom-intro]') && instance) {
      instance.finish();
    }
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector('[data-aspoom-intro]')) {
      instance = null;
      boot();
    }
  });
})();
